// App.tsx - Main application router
import { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useExternalScripts } from "./hooks/useExternalScripts";
import { useGoogleVerification } from "./hooks/useGoogleVerification";
import { useAchievementNotifications } from "./hooks/useAchievementNotifications";
import { useWebVitals } from "./hooks/useWebVitals";
import { useActivityTracker } from "./hooks/useActivityTracker";
import { OrganizationSchema } from "./components/seo/OrganizationSchema";
import { WebSiteSchema } from "./components/seo/WebSiteSchema";
import { PageLoader } from "./components/common/PageLoader";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { CookieBanner } from "./components/common/CookieBanner";
import { PWAInstallPrompt } from "./components/common/PWAInstallPrompt";

// Páginas de entrada e área logada - carregamento imediato
import IndexPreview from "./pages/IndexPreview";
import Login from "./pages/login/Login";
import Register from "./pages/register/Register";
import AuthCallback from "./pages/AuthCallback";

// Candidato - carregamento imediato (área logada)
import CandidateDashboard from "./pages/candidate/Dashboard";
import CandidateJobs from "./pages/candidate/Jobs";
import Achievements from "./pages/candidate/Achievements";
import Practice from "./pages/candidate/Practice";
import ReferralHistory from "./pages/candidate/ReferralHistory";
import Challenges from "./pages/candidate/Challenges";
import Certificates from "./pages/candidate/Certificates";
import GettingStarted from "./pages/GettingStarted";
import { CompleteProfile } from "./components/auth/CompleteProfile";

// Comunidade - carregamento imediato
import Feed from "./pages/candidate/community/Feed";
import Introductions from "./pages/candidate/community/Introductions";
import Learning from "./pages/candidate/community/Learning";
import Questions from "./pages/candidate/community/Questions";

import Datasets from "./pages/candidate/community/Datasets";
import PartnerLanding from "./pages/candidate/community/PartnerLanding";

// Admin - carregamento imediato
import SEOScripts from "./pages/candidate/admin/SEOScripts";
import { BannersPage } from "./pages/candidate/admin/Banners";
import StaticPages from "./pages/candidate/admin/StaticPages";
import ContactSettings from "./pages/candidate/admin/ContactSettings";
import UISettings from "./pages/candidate/admin/UISettings";
import SEOSettings from "./pages/candidate/admin/SEOSettings";
import RoleTest from "./pages/candidate/admin/RoleTest";
import Analytics from "./pages/candidate/admin/Analytics";
import TutorAnalytics from "./pages/candidate/admin/TutorAnalytics";
import QuizGenerator from "./pages/candidate/admin/QuizGenerator";
import AcademyManager from "./pages/candidate/AcademyManager";
import CuratedJobs from "./pages/candidate/admin/CuratedJobs";
import LinkedinImports from "./pages/candidate/admin/LinkedinImports";
import JobDigest from "./pages/candidate/admin/JobDigest";
import GuidesExport from "./pages/candidate/admin/GuidesExport";
import Announcements from "./pages/candidate/admin/Announcements";
import FooterSettings from "./pages/candidate/admin/FooterSettings";
import PartnerPages from "./pages/candidate/admin/PartnerPages";
import SupportFeedbacks from "./pages/candidate/admin/SupportFeedbacks";
import SalesPageEditor from "./pages/candidate/admin/SalesPageEditor";
import PollsAdmin from "./pages/candidate/admin/Polls";

// Empresa - carregamento imediato
import CompanyDashboard from "./pages/company/Dashboard";
import CompanyLogin from "./pages/company/Login";

// Lazy loading - apenas páginas públicas menos frequentes
const Index = lazy(() => import("./pages/Index"));
const Referral = lazy(() => import("./pages/Referral"));
const ForgotPassword = lazy(() => import("./pages/forgot-password/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/reset-password/ResetPassword"));
const About = lazy(() => import("./pages/static/About"));
const Terms = lazy(() => import("./pages/static/Terms"));
const Privacy = lazy(() => import("./pages/static/Privacy"));
const Community = lazy(() => import("./pages/Community"));
const VerifyCertificate = lazy(() => import("./pages/VerifyCertificate"));
const PublicJobsPage = lazy(() => import("./pages/PublicJobsPage"));
const JobDetailPage = lazy(() => import("./pages/JobDetailPage"));
const AnalistaDadosJobsLP = lazy(() => import("./pages/AnalistaDadosJobsLP"));
const AnaliseDadosHub = lazy(() => import("./pages/AnaliseDadosHub"));
const CandidatesLanding = lazy(() => import("./pages/CandidatesLanding"));
const GuiaCarreiraDados2026 = lazy(() => import("./pages/GuiaCarreiraDados2026"));
const Guides = lazy(() => import("./pages/Guides"));
const Glossary = lazy(() => import("./pages/Glossary"));
const CompanySalesLP = lazy(() => import("./pages/CompanySalesLP"));

// Lazy loading - Guias (menos frequentes)
const FirstDataJob = lazy(() => import("./pages/guides/FirstDataJob"));
const DataSalaries = lazy(() => import("./pages/guides/DataSalaries"));
const DataTools2026 = lazy(() => import("./pages/guides/DataTools2026"));
const CareerTransition = lazy(() => import("./pages/guides/CareerTransition"));
const DataCertifications = lazy(() => import("./pages/guides/DataCertifications"));
const PythonVsSQL = lazy(() => import("./pages/guides/PythonVsSQL"));
const DataSalaries2025 = lazy(() => import("./pages/guides/DataSalaries2025"));
const FatalMistakes = lazy(() => import("./pages/guides/FatalMistakes"));
const RemoteInternational = lazy(() => import("./pages/guides/RemoteInternational"));
const DataCareerComparison = lazy(() => import("./pages/guides/DataCareerComparison"));
const TechnicalInterviews = lazy(() => import("./pages/guides/TechnicalInterviews"));
const CloudComparison = lazy(() => import("./pages/guides/CloudComparison"));
const BIToolsComparison = lazy(() => import("./pages/guides/BIToolsComparison"));
const SoftSkillsData = lazy(() => import("./pages/guides/SoftSkillsData"));
const MachineLearningForAnalysts = lazy(() => import("./pages/guides/MachineLearningForAnalysts"));
const MachineLearningIntro = lazy(() => import("./pages/guides/MachineLearningIntro"));
const ChiefAIOfficer = lazy(() => import("./pages/guides/ChiefAIOfficer"));
const DataArchitectures = lazy(() => import("./pages/guides/DataArchitectures"));
// Novo guia - Janeiro 2026
const AIEngineer2026 = lazy(() => import("./pages/guides/AIEngineer2026"));
// 4 novos guias - Janeiro 2026
const MLOps = lazy(() => import("./pages/guides/MLOps"));
const DatabricksVsSnowflake = lazy(() => import("./pages/guides/DatabricksVsSnowflake"));
const PromptEngineering = lazy(() => import("./pages/guides/PromptEngineering"));
const DataProductManager = lazy(() => import("./pages/guides/DataProductManager"));
// 10 novos guias - Janeiro 2026
const IAGenerativaAnaliseDados = lazy(() => import("./pages/guides/IAGenerativaAnaliseDados"));
const GuiaDBT = lazy(() => import("./pages/guides/GuiaDBT"));
const MigracaoExcelPowerBI = lazy(() => import("./pages/guides/MigracaoExcelPowerBI"));
const SQLAvancado = lazy(() => import("./pages/guides/SQLAvancado"));
const PortfolioDados = lazy(() => import("./pages/guides/PortfolioDados"));
const ETLPipelines = lazy(() => import("./pages/guides/ETLPipelines"));
const DataGovernanceLGPD = lazy(() => import("./pages/guides/DataGovernanceLGPD"));
const FreelancerDados = lazy(() => import("./pages/guides/FreelancerDados"));
const RoadmapDataEngineer = lazy(() => import("./pages/guides/RoadmapDataEngineer"));
const RoadmapDataScientist = lazy(() => import("./pages/guides/RoadmapDataScientist"));
const AnaliseDadosPython2026 = lazy(() => import("./pages/guides/AnaliseDadosPython2026"));
const AprenderPowerBI = lazy(() => import("./pages/guides/AprenderPowerBI"));
const GoogleAnalytics4 = lazy(() => import("./pages/guides/GoogleAnalytics4"));
const LookerStudio = lazy(() => import("./pages/guides/LookerStudio"));
const Tableau = lazy(() => import("./pages/guides/Tableau"));
const JupyterNotebook = lazy(() => import("./pages/guides/JupyterNotebook"));
const SQLvsMySQLvsPostgreSQL = lazy(() => import("./pages/guides/SQLvsMySQLvsPostgreSQL"));
// Cluster Análise de Dados - Fevereiro 2026
const AnalistaDados = lazy(() => import("./pages/guides/AnalistaDados"));
const AnaliseDadosIniciantes = lazy(() => import("./pages/guides/AnaliseDadosIniciantes"));
const FerramentasAnaliseDados = lazy(() => import("./pages/guides/FerramentasAnaliseDados"));
const AnaliseDadosExcel = lazy(() => import("./pages/guides/AnaliseDadosExcel"));
// Março 2026
const MulheresDados = lazy(() => import("./pages/guides/MulheresDados"));
const SindromeImpostora = lazy(() => import("./pages/guides/SindromeImpostora"));
const MulheresReferenciaDados = lazy(() => import("./pages/guides/MulheresReferenciaDados"));
const SkillsMulheresDados = lazy(() => import("./pages/guides/SkillsMulheresDados"));
const DiversidadeIA = lazy(() => import("./pages/guides/DiversidadeIA"));
// Abril 2026 - Cluster Transição de Carreira
const MarketingParaDados = lazy(() => import("./pages/guides/MarketingParaDados"));
const FinanceiroParaDados = lazy(() => import("./pages/guides/FinanceiroParaDados"));
const TISuporteParaDados = lazy(() => import("./pages/guides/TISuporteParaDados"));
const AdministracaoParaDados = lazy(() => import("./pages/guides/AdministracaoParaDados"));
const ExplicarTransicaoEntrevista = lazy(() => import("./pages/guides/ExplicarTransicaoEntrevista"));
const TempoEstudoPrimeiroCurriculo = lazy(() => import("./pages/guides/TempoEstudoPrimeiroCurriculo"));
// Abril 2026 - Cluster Crescimento de Carreira
const SairJunior = lazy(() => import("./pages/guides/SairJunior"));
const AnalistaParaEngenheiro = lazy(() => import("./pages/guides/AnalistaParaEngenheiro"));
const SoftSkillsSenior = lazy(() => import("./pages/guides/SoftSkillsSenior"));
const CasoNegocioAumento = lazy(() => import("./pages/guides/CasoNegocioAumento"));
const TrocarEmpresaVsPromocao = lazy(() => import("./pages/guides/TrocarEmpresaVsPromocao"));
const AutoridadeDados = lazy(() => import("./pages/guides/AutoridadeDados"));

function App() {
  useExternalScripts();
  useGoogleVerification();
  useAchievementNotifications();
  useWebVitals();
  useActivityTracker();

  return (
    <>
      <OrganizationSchema />
      <WebSiteSchema />
      <Router>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<IndexPreview />} />
            <Route path="/home-preview" element={<CandidatesLanding />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/r/:code" element={<Referral />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Páginas estáticas públicas */}
            <Route path="/sobre" element={<About />} />
            <Route path="/termos" element={<Terms />} />
            <Route path="/privacidade" element={<Privacy />} />
            <Route path="/comunidade" element={<Community />} />
            <Route path="/verificar" element={<VerifyCertificate />} />
            <Route path="/verificar/:code" element={<VerifyCertificate />} />
            
            {/* Páginas públicas de vagas (SEO) */}
            <Route path="/vagas" element={<PublicJobsPage />} />
            <Route path="/vagas/analista-de-dados" element={<AnalistaDadosJobsLP />} />
            <Route path="/vagas/:slug" element={<JobDetailPage />} />
            
            {/* Hub SEO - Análise de Dados */}
            <Route path="/analise-de-dados" element={<AnaliseDadosHub />} />
            
            {/* Landing Pages de Captação */}
            <Route path="/guia-carreira-dados-2026" element={<GuiaCarreiraDados2026 />} />
            
            {/* Glossário de Dados */}
            
            {/* LP Vendas para Empresas */}
            <Route path="/para-empresas" element={<CompanySalesLP />} />
            <Route path="/glossario" element={<Glossary />} />
            
            {/* Guias */}
            <Route path="/guias" element={<Guides />} />
            <Route path="/guias/salario-analista-dados-brasil-2025" element={<DataSalaries2025 />} />
            <Route path="/guias/7-erros-que-impedem-vaga-dados" element={<FatalMistakes />} />
            <Route path="/guias/trabalho-remoto-internacional-dados" element={<RemoteInternational />} />
            <Route path="/guias/primeira-vaga-dados-2025" element={<FirstDataJob />} />
            <Route path="/guias/salarios-dados-2026" element={<DataSalaries />} />
            <Route path="/guias/ferramentas-dados-2026" element={<DataTools2026 />} />
            <Route path="/guias/transicao-carreira-dados" element={<CareerTransition />} />
            <Route path="/guias/certificacoes-dados-2026" element={<DataCertifications />} />
            <Route path="/guias/python-vs-sql" element={<PythonVsSQL />} />
            <Route path="/guias/data-engineer-vs-scientist-vs-analyst" element={<DataCareerComparison />} />
            <Route path="/guias/entrevistas-tecnicas-dados" element={<TechnicalInterviews />} />
            <Route path="/guias/aws-vs-azure-vs-gcp-dados" element={<CloudComparison />} />
            <Route path="/guias/power-bi-vs-tableau-vs-looker" element={<BIToolsComparison />} />
            <Route path="/guias/soft-skills-dados-2025" element={<SoftSkillsData />} />
            <Route path="/guias/machine-learning-analistas-dados-2025" element={<MachineLearningForAnalysts />} />
            <Route path="/guias/o-que-e-machine-learning-dados" element={<MachineLearningIntro />} />
            <Route path="/guias/chief-ai-officer-caio" element={<ChiefAIOfficer />} />
            <Route path="/guias/data-warehouse-lake-lakehouse-mesh" element={<DataArchitectures />} />
            {/* Novo guia - Janeiro 2026 */}
            <Route path="/guias/engenheiro-ia-2026" element={<AIEngineer2026 />} />
            {/* 4 novos guias - Janeiro 2026 */}
            <Route path="/guias/mlops-machine-learning-producao" element={<MLOps />} />
            <Route path="/guias/databricks-vs-snowflake" element={<DatabricksVsSnowflake />} />
            <Route path="/guias/prompt-engineering-profissionais-dados" element={<PromptEngineering />} />
            <Route path="/guias/data-product-manager" element={<DataProductManager />} />
            {/* 10 novos guias - Janeiro 2026 */}
            <Route path="/guias/ia-generativa-analise-dados" element={<IAGenerativaAnaliseDados />} />
            <Route path="/guias/guia-dbt-analytics-engineering" element={<GuiaDBT />} />
            <Route path="/guias/migracao-excel-power-bi" element={<MigracaoExcelPowerBI />} />
            <Route path="/guias/sql-avancado-analistas" element={<SQLAvancado />} />
            <Route path="/guias/portfolio-dados-github" element={<PortfolioDados />} />
            <Route path="/guias/etl-pipelines-apache-spark" element={<ETLPipelines />} />
            <Route path="/guias/data-governance-lgpd" element={<DataGovernanceLGPD />} />
            <Route path="/guias/freelancer-dados-consultoria" element={<FreelancerDados />} />
            <Route path="/guias/roadmap-data-engineer-2026" element={<RoadmapDataEngineer />} />
            <Route path="/guias/roadmap-data-scientist-2026" element={<RoadmapDataScientist />} />
            {/* 7 novos guias SEO - Janeiro 2026 */}
            <Route path="/guias/analise-dados-python-2026" element={<AnaliseDadosPython2026 />} />
            <Route path="/guias/como-aprender-power-bi" element={<AprenderPowerBI />} />
            <Route path="/guias/google-analytics-4-analistas" element={<GoogleAnalytics4 />} />
            <Route path="/guias/looker-studio-guia-completo" element={<LookerStudio />} />
            <Route path="/guias/aprender-tableau-2026" element={<Tableau />} />
            <Route path="/guias/jupyter-notebook-dados" element={<JupyterNotebook />} />
            <Route path="/guias/sql-vs-mysql-vs-postgresql" element={<SQLvsMySQLvsPostgreSQL />} />
            {/* Cluster Análise de Dados - Fevereiro 2026 */}
            <Route path="/guias/o-que-faz-analista-dados" element={<AnalistaDados />} />
            <Route path="/guias/analise-dados-iniciantes" element={<AnaliseDadosIniciantes />} />
            <Route path="/guias/ferramentas-analise-dados" element={<FerramentasAnaliseDados />} />
            <Route path="/guias/analise-dados-excel" element={<AnaliseDadosExcel />} />
            <Route path="/guias/mulheres-analise-dados" element={<MulheresDados />} />
            <Route path="/guias/sindrome-impostora-dados" element={<SindromeImpostora />} />
            <Route path="/guias/mulheres-referencia-dados" element={<MulheresReferenciaDados />} />
            <Route path="/guias/skills-mulheres-dados" element={<SkillsMulheresDados />} />
            <Route path="/guias/diversidade-ia-equipes-diversas" element={<DiversidadeIA />} />
            {/* Cluster Transição de Carreira - Abril 2026 */}
            <Route path="/guias/marketing-para-dados" element={<MarketingParaDados />} />
            <Route path="/guias/financeiro-para-dados" element={<FinanceiroParaDados />} />
            <Route path="/guias/ti-suporte-para-dados" element={<TISuporteParaDados />} />
            <Route path="/guias/administracao-para-dados" element={<AdministracaoParaDados />} />
            <Route path="/guias/explicar-transicao-carreira-entrevista" element={<ExplicarTransicaoEntrevista />} />
            <Route path="/guias/tempo-estudo-primeiro-curriculo-dados" element={<TempoEstudoPrimeiroCurriculo />} />
            {/* Cluster Crescimento de Carreira - Abril 2026 */}
            <Route path="/guias/sair-junior-sem-esperar-2-anos" element={<SairJunior />} />
            <Route path="/guias/analista-para-engenheiro-dados" element={<AnalistaParaEngenheiro />} />
            <Route path="/guias/soft-skills-promocao-senior-dados" element={<SoftSkillsSenior />} />
            <Route path="/guias/caso-negocio-pedir-aumento-dados" element={<CasoNegocioAumento />} />
            <Route path="/guias/trocar-empresa-vs-promocao-dados" element={<TrocarEmpresaVsPromocao />} />
            <Route path="/guias/autoridade-dados-sem-ser-tecnico" element={<AutoridadeDados />} />

            <Route path="/complete-profile" element={
              <ProtectedRoute>
                <CompleteProfile />
              </ProtectedRoute>
            } />
            <Route path="/candidate/dashboard" element={
              <ProtectedRoute requiredUserType="candidate">
                <CandidateDashboard />
              </ProtectedRoute>
            } />
            <Route path="/candidate/jobs" element={
              <ProtectedRoute requiredUserType="candidate">
                <CandidateJobs />
              </ProtectedRoute>
            } />
            <Route path="/candidate/achievements" element={
              <ProtectedRoute requiredUserType="candidate">
                <Achievements />
              </ProtectedRoute>
            } />
            <Route path="/candidate/practice" element={
              <ProtectedRoute requiredUserType="candidate">
                <Practice />
              </ProtectedRoute>
            } />
            <Route path="/candidate/referrals" element={
              <ProtectedRoute requiredUserType="candidate">
                <ReferralHistory />
              </ProtectedRoute>
            } />
            <Route path="/candidate/challenges" element={
              <ProtectedRoute requiredUserType="candidate">
                <Challenges />
              </ProtectedRoute>
            } />
            <Route path="/candidate/certificates" element={
              <ProtectedRoute requiredUserType="candidate">
                <Certificates />
              </ProtectedRoute>
            } />
            <Route path="/candidate/jobs/community/feed" element={
              <ProtectedRoute requiredUserType="candidate">
                <Feed />
              </ProtectedRoute>
            } />
            <Route path="/candidate/jobs/community/introductions" element={
              <ProtectedRoute requiredUserType="candidate">
                <Introductions />
              </ProtectedRoute>
            } />
            <Route path="/candidate/jobs/community/learning" element={
              <ProtectedRoute requiredUserType="candidate">
                <Learning />
              </ProtectedRoute>
            } />
            <Route path="/candidate/jobs/community/questions" element={
              <ProtectedRoute requiredUserType="candidate">
                <Questions />
              </ProtectedRoute>
            } />
            <Route path="/candidate/jobs/community/datasets" element={
              <ProtectedRoute requiredUserType="candidate">
                <Datasets />
              </ProtectedRoute>
            } />
            
            {/* Rotas alias para notificações */}
            <Route path="/candidate/community/feed" element={
              <ProtectedRoute requiredUserType="candidate">
                <Feed />
              </ProtectedRoute>
            } />
            <Route path="/candidate/community/introductions" element={
              <ProtectedRoute requiredUserType="candidate">
                <Introductions />
              </ProtectedRoute>
            } />
            <Route path="/candidate/community/learning" element={
              <ProtectedRoute requiredUserType="candidate">
                <Learning />
              </ProtectedRoute>
            } />
            <Route path="/candidate/community/questions" element={
              <ProtectedRoute requiredUserType="candidate">
                <Questions />
              </ProtectedRoute>
            } />
            <Route path="/candidate/community/datasets" element={
              <ProtectedRoute requiredUserType="candidate">
                <Datasets />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/seo-scripts" element={
              <ProtectedRoute requiredUserType="candidate">
                <SEOScripts />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/banners" element={
              <ProtectedRoute requiredUserType="candidate">
                <BannersPage />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/static-pages" element={
              <ProtectedRoute requiredUserType="candidate">
                <StaticPages />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/contact-settings" element={
              <ProtectedRoute requiredUserType="candidate">
                <ContactSettings />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/ui-settings" element={
              <ProtectedRoute requiredUserType="candidate">
                <UISettings />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/seo-settings" element={
              <ProtectedRoute requiredUserType="candidate">
                <SEOSettings />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/role-test" element={
              <ProtectedRoute requiredUserType="candidate">
                <RoleTest />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/analytics" element={
              <ProtectedRoute requiredUserType="candidate">
                <Analytics />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/curated-jobs" element={
              <ProtectedRoute requiredUserType="candidate">
                <CuratedJobs />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/linkedin-imports" element={
              <ProtectedRoute requiredUserType="candidate">
                <LinkedinImports />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/job-digest" element={
              <ProtectedRoute requiredUserType="candidate">
                <JobDigest />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/guides-export" element={
              <ProtectedRoute requiredUserType="candidate">
                <GuidesExport />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/announcements" element={
              <ProtectedRoute requiredUserType="candidate">
                <Announcements />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/tutor-analytics" element={
              <ProtectedRoute requiredUserType="candidate">
                <TutorAnalytics />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/quiz-generator" element={
              <ProtectedRoute requiredUserType="candidate">
                <QuizGenerator />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/academy-manager" element={
              <ProtectedRoute requiredUserType="candidate">
                <AcademyManager />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/footer-settings" element={
              <ProtectedRoute requiredUserType="candidate">
                <FooterSettings />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/partner-pages" element={
              <ProtectedRoute requiredUserType="candidate">
                <PartnerPages />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/support-feedbacks" element={
              <ProtectedRoute requiredUserType="candidate">
                <SupportFeedbacks />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/sales-page" element={
              <ProtectedRoute requiredUserType="candidate">
                <SalesPageEditor />
              </ProtectedRoute>
            } />
            <Route path="/candidate/admin/polls" element={
              <ProtectedRoute requiredUserType="candidate">
                <PollsAdmin />
              </ProtectedRoute>
            } />
            <Route path="/candidate/jobs/community/parceiros/:slug" element={
              <ProtectedRoute requiredUserType="candidate">
                <PartnerLanding />
              </ProtectedRoute>
            } />
            <Route path="/comece-aqui" element={
              <ProtectedRoute requiredUserType="candidate">
                <GettingStarted />
              </ProtectedRoute>
            } />

            {/* Rotas protegidas para empresas */}
            <Route path="/company/dashboard" element={
              <ProtectedRoute requiredUserType="company">
                <CompanyDashboard />
              </ProtectedRoute>
            } />
            <Route path="/company/login" element={<CompanyLogin />} />
          </Routes>
        </Suspense>
        <CookieBanner />
        <PWAInstallPrompt />
      </Router>
    </>
  );
}

export default App;
